import React from 'react'
import axios from 'axios'
import { Button } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import DeleteIcon from 'components/icons/DeleteIcon'
import {
  FieldArrayPath, FieldPathByValue, FieldValues, useFormContext,
} from 'react-hook-form'
import { FileType } from 'features/declaration/models/schemas/fileSchemas'
import useSoftDeleteFieldArray from 'shared/rhf/useSoftDeleteFieldArray'
import fetchSignedFileUrl from 'shared/file/api/fetchSignedFileUrl'
import { FileFolder } from 'shared/file/models/types'
import reportApiFailure from 'shared/api/reportApiFailure'
import { isAxiosError } from 'shared/api/isAxiosError'
import { saveBlobAsFile } from 'shared/file/saveBlobAsFile'
import { saveUrlAsFile } from 'shared/file/saveUrlAsFile'

type FileArrayPath<TForm extends FieldValues> = Extract<FieldArrayPath<TForm>, FieldPathByValue<TForm, FileType[]>>

interface DocumentFileNamesProps<TForm extends FieldValues> {
  name: FileArrayPath<TForm>
}

function DocumentFiles<
  TForm extends FieldValues,
>({ name }: DocumentFileNamesProps<TForm>) {
  const { control } = useFormContext<TForm>()
  const {
    visibleFields,
    softRemove,
  } = useSoftDeleteFieldArray<TForm, FileType, FileArrayPath<TForm>>({
    control,
    name: name as FileArrayPath<TForm>,
  })

  const { t } = useTranslation()

  const saveFile = async ({ fileBlob, fileName, uuid }: FileType) => {
    if (fileBlob !== null) {
      saveBlobAsFile(fileBlob, fileName)
      return
    }
    if (uuid === null) {
      return
    }
    try {
      const [folder, fileUuid] = uuid.split('/')
      const url = await fetchSignedFileUrl({ folder: folder as FileFolder, fileUuid })
      await saveUrlAsFile(url, fileName)
    } catch (e) {
      if ((isAxiosError(e) && e.response?.status === 401) || axios.isCancel(e)) {
        return
      }
      reportApiFailure(e, () => toast.error(t('errors.fileDownloadFailed')))
    }
  }

  return (
    <div className="row mb-1" key="document-files">
      <div className="col-12 d-flex flex-column">
        {(visibleFields.map((fieldWithIndex) => {
          const file = fieldWithIndex.field

          return (
            (
              <div
                className="d-flex justify-content-end align-items-center"
                key={`document-file-${fieldWithIndex.field.id}-${file.sequenceNumber}-${file.fileName}`}
                hidden={file.deleted}
              >
                <Button
                  type="button"
                  variant="link"
                  className="d-flex overflow-auto align-items-baseline no-text-transform"
                  onClick={() => {
                    saveFile(file)
                  }}
                >
                  <i className="fal d-inline-block fa-file me-2" />
                  <span className="overflow-ellipsis">
                    {file.fileName}
                  </span>
                </Button>
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="m-1 btn-trash"
                  onClick={() => {
                    softRemove(fieldWithIndex.index)
                  }}
                >
                  <DeleteIcon />
                </Button>
              </div>
            )
          )
        }))}
      </div>
    </div>
  )
}

export default DocumentFiles
